import React from "react";
import { FiAlertTriangle, FiHelpCircle, FiX } from "react-icons/fi";
import { Button } from "./Button";

export type ConfirmDialogTone = "neutral" | "danger" | "warning";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: ConfirmDialogTone;
  isLoading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const toneStyles: Record<
  ConfirmDialogTone,
  { icon: string; button: "primary" | "danger" | "warning" }
> = {
  neutral: { icon: "bg-primary-50 text-primary-700", button: "primary" },
  danger: { icon: "bg-rose-50 text-rose-700", button: "danger" },
  warning: { icon: "bg-amber-50 text-amber-700", button: "warning" },
};

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancelar",
  tone = "neutral",
  isLoading = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const titleId = React.useId();
  const descriptionId = React.useId();

  if (!open) return null;

  const styles = toneStyles[tone];

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/35 p-0 backdrop-blur-[1px] sm:items-center sm:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className="max-h-[calc(100dvh-1rem)] w-full overflow-y-auto rounded-t-3xl border border-slate-200 bg-white p-5 shadow-2xl sm:max-h-[calc(100dvh-3rem)] sm:max-w-md sm:rounded-2xl sm:p-6"
      >
        <div className="flex items-start gap-3">
          <div
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-xl ${styles.icon}`}
            aria-hidden="true"
          >
            {tone === "neutral" ? <FiHelpCircle /> : <FiAlertTriangle />}
          </div>
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-lg font-bold text-slate-950">
              {title}
            </h2>
            {description ? (
              <div
                id={descriptionId}
                className="mt-1 text-sm font-medium leading-6 text-slate-500"
              >
                {description}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            aria-label="Fechar"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiX />
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            fullWidth
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={styles.button}
            onClick={onConfirm}
            isLoading={isLoading}
            fullWidth
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
